import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FileText,
  Briefcase,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Zap,
  Flame,
  Euro,
  Building2,
  Megaphone,
  CheckCheck,
  Network,
  BarChart3,
  MessageSquare,
  RotateCw,
  ShoppingBag,
  Phone,
  LineChart,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import GlobalSearch from './GlobalSearch';
import AiAssistant from './AiAssistant';
import PwaSetup from './PwaSetup';
import ThemeToggle from './ThemeToggle';
import BottomNav from './BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [rinnoviCount, setRinnoviCount] = useState(0);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1'; } catch (e) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch (e) {}
  }, [sidebarCollapsed]);
  
  const [expandedSections, setExpandedSections] = useState({
    vendite: true,
    admin: true,
    squadra: true,
    comunicazioni: false,
    callcenter: false,
    settings: false,
  });

  const isSubAgent = user?.ruolo === 'sub_agent';

  const fetchUnread = useCallback(async () => {
    try {
      if (isSubAgent) {
        const res = await axios.get(`${API_URL}/api/notifications/unread-count`);
        setUnreadCount(res.data.unread);
      } else {
        const res = await axios.get(`${API_URL}/api/announcements/unread-count`);
        setUnreadCount(res.data.unread);
      }
    } catch (e) {}
    try {
      const res2 = await axios.get(`${API_URL}/api/chat/unread-count`);
      setChatUnread(res2.data.count);
    } catch (e) {}
    try {
      const res3 = await axios.get(`${API_URL}/api/rinnovi/conteggio`);
      setRinnoviCount(res3.data.count);
    } catch (e) {}
  }, [isSubAgent]);

  const fetchNotifications = async () => {
    try {
      if (isSubAgent) {
        const res = await axios.get(`${API_URL}/api/notifications`);
        setNotifications(res.data.slice(0, 10));
      } else {
        const res = await axios.get(`${API_URL}/api/announcements`);
        setNotifications(res.data.slice(0, 10));
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const handleBellClick = async () => {
    if (!showNotifPanel) await fetchNotifications();
    setShowNotifPanel(!showNotifPanel);
  };

  const handleMarkAllRead = async () => {
    try {
      if (isSubAgent) await axios.put(`${API_URL}/api/notifications/read-all`);
      else await axios.put(`${API_URL}/api/announcements/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, letto: true })));
    } catch (e) {}
  };

  // Menu structure - simplified & renamed in Italian, flat groups (no nested submenus)
  const menuCategories = [
    {
      id: 'main',
      label: null,
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'sub_agent', 'back_office', 'interno', 'operatore_call'] },
      ],
    },
    {
      id: 'vendite',
      label: 'Vendite',
      icon: ShoppingBag,
      items: [
        { name: 'Lead', href: '/leads', icon: UserPlus, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
        { name: 'Clienti', href: '/clients', icon: Users, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
        { name: 'Pratiche', href: '/contracts', icon: FileText, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
        { name: 'Rinnovi', href: '/rinnovi', icon: RotateCw, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'], badgeKey: 'rinnovi' },
        { name: 'Comparatore Offerte', href: '/comparatore', icon: LineChart, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
      ],
    },
    {
      id: 'comunicazioni',
      label: 'Comunicazioni',
      icon: MessageSquare,
      items: [
        { name: 'Chat', href: '/chat', icon: MessageSquare, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'], badgeKey: 'chat' },
        { name: 'Avvisi', href: '/avvisi', icon: Megaphone, roles: ['super_admin', 'back_office', 'interno'] },
      ],
    },
    {
      id: 'callcenter',
      label: 'Call Center',
      icon: Phone,
      items: [
        { name: 'Contatti', href: '/callcenter', icon: Phone, roles: ['super_admin', 'sub_agent', 'operatore_call'] },
      ],
    },
    {
      id: 'admin',
      label: 'Amministrazione',
      icon: Euro,
      items: [
        { name: 'Provvigioni', href: '/provvigioni', icon: Euro, roles: ['super_admin'] },
        { name: 'Listino Provvigioni', href: '/commissioni', icon: FileText, roles: ['super_admin'] },
        { name: 'Le Mie Provvigioni', href: '/mie-provvigioni', icon: Euro, roles: ['sub_agent'] },
        { name: 'Report Mensile', href: '/report-mensile', icon: BarChart3, roles: ['super_admin'] },
        { name: 'Compagnie', href: '/companies', icon: Building2, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
      ],
    },
    {
      id: 'squadra',
      label: 'Squadra',
      icon: Users,
      items: [
        { name: 'Sub-agenti', href: '/agents', icon: Briefcase, roles: ['super_admin'] },
        { name: 'Albero Rete', href: '/albero-rete', icon: Network, roles: ['super_admin'] },
        { name: 'Utenti', href: '/users', icon: Users, roles: ['super_admin', 'sub_agent', 'back_office', 'interno'] },
        { name: 'Reti di Vendita', href: '/reti-vendita', icon: Network, roles: ['super_admin', 'back_office', 'interno'] },
      ],
    },
    {
      id: 'settings',
      label: 'Impostazioni',
      icon: Settings,
      items: [
        { name: 'Impostazioni', href: '/settings', icon: Settings, roles: ['super_admin'] },
      ],
    },
  ];

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const handleLogout = () => { logout(); navigate('/login'); };

  const getRoleLabel = (role) => ({
    super_admin: 'Super Admin', admin: 'Manager', interno: 'Utente Interno',
    sub_agent: 'Sub-agente', back_office: 'Back Office',
  }[role] || role);

  const getInitials = (nome, cognome) => `${nome?.[0] || ''}${cognome?.[0] || ''}`.toUpperCase();

  const getBadgeCount = (badgeKey) => {
    if (badgeKey === 'chat') return chatUnread;
    if (badgeKey === 'rinnovi') return rinnoviCount;
    return 0;
  };

  const isItemActive = (href) => {
    if (!href) return false;
    return location.pathname === href || (href !== '/dashboard' && location.pathname.startsWith(href));
  };

  const renderNavItem = (item) => {
    if (!hasRole(item.roles)) return null;
    const isActive = isItemActive(item.href);
    const badgeCount = getBadgeCount(item.badgeKey);

    const linkContent = (
      <>
        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
        {!sidebarCollapsed && <span className="text-[13px] truncate">{item.name}</span>}
        {!sidebarCollapsed && badgeCount > 0 && (
          <span className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold ${
            item.badgeKey === 'chat' ? 'bg-sky-500' : 'bg-amber-500'
          }`}>{badgeCount}</span>
        )}
        {sidebarCollapsed && badgeCount > 0 && (
          <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
            item.badgeKey === 'chat' ? 'bg-sky-500' : 'bg-amber-500'
          }`} />
        )}
      </>
    );

    const linkClasses = `relative flex items-center gap-2 rounded-xl text-sm transition-colors ${
      sidebarCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-1.5'
    } ${
      isActive
        ? 'bg-sky-50 text-sky-700 font-medium'
        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
    }`;

    const link = (
      <Link
        key={item.name}
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={linkClasses}
        onClick={() => setSidebarOpen(false)}
      >
        {linkContent}
      </Link>
    );

    if (sidebarCollapsed) {
      return (
        <Tooltip key={item.name} delayDuration={100}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
            {item.name}
            {badgeCount > 0 && <span className="ml-2 text-amber-300 font-bold">{badgeCount}</span>}
          </TooltipContent>
        </Tooltip>
      );
    }
    return link;
  };

  const renderCategory = (category) => {
    const visibleItems = category.items.filter(item => hasRole(item.roles));
    if (visibleItems.length === 0) return null;

    if (!category.label) {
      return (
        <div key={category.id} className="mb-3">
          {visibleItems.map(renderNavItem)}
        </div>
      );
    }

    const isExpanded = expandedSections[category.id] !== false;
    const hasActiveItem = visibleItems.some(item => isItemActive(item.href));

    if (sidebarCollapsed) {
      return (
        <div key={category.id} className="mb-2 space-y-0.5">
          {visibleItems.map(renderNavItem)}
          {category.id !== 'settings' && <div className="my-1.5 h-px bg-slate-200 mx-2" />}
        </div>
      );
    }

    return (
      <div key={category.id} className="mb-2">
        <button
          onClick={() => toggleSection(category.id)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl transition-colors ${
            hasActiveItem
              ? 'text-slate-900'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
          }`}
          data-testid={`section-${category.id}`}
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
          <category.icon className="w-4 h-4" strokeWidth={1.5} />
          <span className="font-semibold text-[11px] uppercase tracking-wider">{category.label}</span>
        </button>

        {isExpanded && (
          <div className="mt-0.5 ml-3 pl-2 border-l border-slate-200 space-y-0.5">
            {visibleItems.map(renderNavItem)}
          </div>
        )}
      </div>
    );
  };

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainPad = sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
    <TooltipProvider>
      <div className="min-h-[100dvh] bg-slate-50 dark:bg-slate-950">
        {sidebarOpen && (
          <div className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar - LIGHT theme */}
        <aside className={`
          fixed top-0 left-0 z-50 h-full ${sidebarWidth} bg-white border-r border-slate-200 transform transition-all duration-200 ease-in-out safe-top safe-left
          lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Logo */}
          <div className={`flex items-center border-b border-slate-100 ${sidebarCollapsed ? 'justify-center px-2 py-4' : 'gap-3 px-5 py-4'}`}>
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 shadow-sm shrink-0">
              <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="text-[15px] font-bold text-slate-900 tracking-tight truncate">F.lli Schiliro'</h1>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Luce & Gas</p>
              </div>
            )}
            <button
              className="lg:hidden text-slate-400 hover:text-slate-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Collapse toggle */}
          <div className="hidden lg:flex justify-end px-2 pt-2">
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="sidebar-collapse-btn"
                >
                  {sidebarCollapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                {sidebarCollapsed ? 'Espandi menu' : 'Comprimi menu'}
              </TooltipContent>
            </Tooltip>
          </div>

          <nav className={`py-3 space-y-1 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-3'}`} style={{ maxHeight: 'calc(100dvh - 180px)' }}>
            {menuCategories.map(renderCategory)}
          </nav>

          {!sidebarCollapsed && (
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5 text-amber-500">
                  <Zap className="w-4 h-4" strokeWidth={2} />
                  <span className="text-slate-500">Luce</span>
                </div>
                <div className="flex items-center gap-1.5 text-sky-500">
                  <Flame className="w-4 h-4" strokeWidth={2} />
                  <span className="text-slate-500">Gas</span>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className={`${mainPad} overflow-x-hidden transition-all duration-200`}>
          <header className="sticky top-0 z-30 bg-white border-b border-slate-200 dark:bg-slate-900 dark:border-slate-800 safe-top safe-left safe-right">
            <div className="flex items-center justify-between px-4 py-3 lg:px-8 min-h-[56px]">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>

              <h2 className="lg:hidden text-lg font-semibold text-slate-900 dark:text-slate-100">
                {menuCategories.flatMap(c => c.items).find(n => n.href && location.pathname.startsWith(n.href))?.name || 'Dashboard'}
              </h2>

              <div className="hidden lg:block flex-1 max-w-md">
                <GlobalSearch />
              </div>

              <div className="flex items-center gap-3">
                <ThemeToggle />
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-slate-600 hover:text-slate-900 rounded-xl dark:text-slate-300 dark:hover:text-white"
                    data-testid="notifications-btn"
                    onClick={handleBellClick}
                  >
                    <Bell className="w-5 h-5" />
                    <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 ${unreadCount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} />
                  </Button>

                  {showNotifPanel && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowNotifPanel(false)} />
                      <div className="absolute right-0 top-12 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="notif-panel">
                        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                          <h3 className="font-semibold text-slate-900 text-sm">{isSubAgent ? 'Notifiche' : 'Avvisi'}</h3>
                          <div className="flex gap-2">
                            {unreadCount > 0 && (
                              <button onClick={handleMarkAllRead} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1">
                                <CheckCheck className="w-3.5 h-3.5" /> Segna tutti letti
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <p className="text-center text-slate-400 text-sm py-8">{isSubAgent ? 'Nessuna notifica' : 'Nessun avviso'}</p>
                          ) : (
                            notifications.map(n => (
                              <div
                                key={n.id}
                                className={`px-4 py-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${!n.letto ? 'bg-sky-50/50' : ''}`}
                                onClick={async () => {
                                  if (!n.letto) {
                                    if (isSubAgent) await axios.put(`${API_URL}/api/notifications/${n.id}/read`);
                                    else await axios.put(`${API_URL}/api/announcements/${n.id}/read`);
                                    setNotifications(prev => prev.map(x => x.id === n.id ? {...x, letto: true} : x));
                                    setUnreadCount(prev => Math.max(0, prev - 1));
                                  }
                                  if (isSubAgent && n.entita_tipo === 'contract' && n.entita_id) {
                                    setShowNotifPanel(false);
                                    navigate(`/contracts/${n.entita_id}`);
                                  }
                                }}
                              >
                                <div className="flex items-start gap-2">
                                  {!n.letto && <span className="mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{n.titolo}</p>
                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.messaggio}</p>
                                    <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString('it-IT', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        {!isSubAgent && (
                          <div className="border-t px-4 py-2 bg-slate-50">
                            <button
                              onClick={() => { setShowNotifPanel(false); navigate('/avvisi'); }}
                              className="text-xs text-sky-600 hover:text-sky-700 font-medium w-full text-center"
                            >
                              Vedi tutti gli avvisi
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 px-2 rounded-xl"
                      data-testid="user-menu-btn"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-slate-200 text-slate-700 text-sm">
                          {getInitials(user?.nome, user?.cognome)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden md:block text-left">
                        <p className="text-sm font-medium text-slate-900">{user?.nome} {user?.cognome}</p>
                        <p className="text-xs text-slate-500">{getRoleLabel(user?.ruolo)}</p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-500 hidden md:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.nome} {user?.cognome}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      Profilo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                      <Settings className="w-4 h-4 mr-2" />
                      Impostazioni
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 focus:text-red-600"
                      data-testid="logout-menu-item"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Esci
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="p-4 lg:p-8 safe-bottom safe-left safe-right pb-24 lg:pb-8">
            {children}
          </main>
        </div>
        <BottomNav onOpenMenu={() => setSidebarOpen(true)} rinnoviCount={rinnoviCount} />
        <AiAssistant />
        <PwaSetup />
      </div>
    </TooltipProvider>
  );
};

export default Layout;
